import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  useTheme,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import PreviewIcon from '@mui/icons-material/Preview';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useRole } from "app/context/RoleContext";
import { useScope } from "app/context/ScopeContext";
import { useResource } from "app/context/ResourceContext";
import DualListMAIQ from 'app/components/DualListMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import CopyToClipboard from 'app/components/CopyToClipboard';
import CellChipStatus from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/components/cells/CellChipStatus';
import ScopeDetailsModal from '../scope/ScopeDetailsModal';
import { formatToLocalTime } from 'utils/dateUtils';
import { Role, Scope, SidebarNavigationItem, SidebarNavigationGroup, Resource } from '../../types';
import { getMaterialIconComponent } from 'app/components/MaterialIconMap';
import { getMuiIconComponent } from 'app/utils/muiIconsList';
import SidebarManagement from './SidebarManagement';

function resolveSidebarIcon(iconName: string): React.ReactElement | null {
  const mui = getMuiIconComponent(iconName);
  if (mui) return mui;
  const fallback = getMaterialIconComponent(iconName);
  return fallback ?? null;
}

interface RoleDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: Role;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

const RoleDetailsModal: React.FC<RoleDetailsModalProps> = ({ open, onClose, data, setRefreshKey }) => {
  const theme = useTheme();
  const { editRole, deleteRole } = useRole();
  const { scopes, getAllScopes } = useScope();
  const { resources: contextResources, getAllResources } = useResource();
  const resources = Array.isArray(contextResources) ? contextResources : [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: window.innerWidth / 2 - 300, y: 100 });
  const [previewHoveredGroupIndex, setPreviewHoveredGroupIndex] = useState<number | null>(null);
  const [previewSize, setPreviewSize] = useState({ width: 600, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const previewHeaderRef = React.useRef<HTMLDivElement>(null);
  const resizeHandleRef = React.useRef<HTMLDivElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [error, setError] = useState<string>("");
  const [selectedScope, setSelectedScope] = useState<Scope | null>(null);

  const [role, setRole] = useState<Role>(data);
  const [originalData, setOriginalData] = useState<Role>(data);
  const [roleScopes, setRoleScopes] = useState<any[]>([]);
  const [roleResources, setRoleResources] = useState<Resource[]>([]);
  const [expandedScopes, setExpandedScopes] = useState(false);
  const [expandedAllPages, setExpandedAllPages] = useState(false);
  const [expandedSidebarResources, setExpandedSidebarResources] = useState(false);
  const [expandedOtherResources, setExpandedOtherResources] = useState(false);

  const isNestedNav = (nav: Role['sidebar_navigation']): nav is (SidebarNavigationItem | SidebarNavigationGroup)[] =>
    !!nav?.length && nav.some((n) => typeof n === 'object' && n !== null && 'items' in n && Array.isArray((n as SidebarNavigationGroup).items));
  const sidebarNavCount = (nav: Role['sidebar_navigation']) => {
    if (!nav?.length) return 0;
    const countRec = (nodes: (SidebarNavigationItem | SidebarNavigationGroup)[]): number =>
      nodes.reduce((s, n) => s + ('link' in n ? 1 : countRec(n.items)), 0);
    if (isNestedNav(nav)) return countRec(nav);
    return (nav as SidebarNavigationItem[]).length;
  };
  const sidebarNavFlat = (nav: Role['sidebar_navigation']): SidebarNavigationItem[] => {
    if (!nav?.length) return [];
    const flatRec = (nodes: (SidebarNavigationItem | SidebarNavigationGroup)[]): SidebarNavigationItem[] =>
      nodes.flatMap((n) => ('link' in n ? [n] : flatRec(n.items)));
    if (isNestedNav(nav)) return flatRec(nav);
    return nav as SidebarNavigationItem[];
  };

  // Get all scopes and resources when modal opens
  useEffect(() => {
    if (open) {
      getAllScopes();
      getAllResources();
    }
  }, [open]);

  // Update role when data changes
  useEffect(() => {
    setRole(data);
    setOriginalData(data);
  }, [data]);

  // Find role scopes when scopes data is loaded (role.scopes can be ids or populated objects)
  useEffect(() => {
    if (scopes.length > 0 && role._id) {
      const roleScopeObjects = scopes.filter(scope =>
        role.scopes && role.scopes.some((roleScope: { _id?: string } | string) =>
          typeof roleScope === 'string' ? roleScope === scope._id : roleScope._id === scope._id
        )
      );
      setRoleScopes(roleScopeObjects);
    }
  }, [scopes, role._id, role.scopes]);

  // Find role connected resources when resources data is loaded
  useEffect(() => {
    if (resources.length > 0 && role._id) {
      const cr = role.connected_resources || [];
      const roleResourceObjects = resources.filter(r =>
        cr.some((crItem: { _id?: string; path?: string; component?: string }) =>
            typeof crItem === 'string'
            ? crItem === r._id
            : (crItem._id ? crItem._id === r._id : (crItem.component === r.component && (crItem.path ?? '') === (r.path ?? '')))
        )
      );
      setRoleResources(roleResourceObjects);
    }
  }, [resources, role._id, role.connected_resources]);

  const scopeId = (roleScope: { _id?: string } | string | null | undefined): string | undefined => {
    if (roleScope == null) return undefined;
    return typeof roleScope === 'string' ? roleScope : roleScope._id;
  };

  // Convert scope objects to DualListMAIQ format
  const convertedScopes = (scopeObjects: any[]) => {
    return (scopeObjects ?? []).map(scope => ({
      id: scope?._id ?? '',
      label: scope?.name || scope?.scope || '—',
      secondaryLabel: scope?.scope || undefined,
    }));
  };

  // Convert resource objects to DualListMAIQ format
  const convertedResources = (resourceObjects: Resource[]) => {
    return (resourceObjects ?? []).map(r => ({
      id: r?._id ?? '',
      label: r?.component || r?.name || '—',
      secondaryLabel: r?.path || undefined,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setRole(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleScopeUpdate = (updatedScopes: any[]) => {
    // Extract scope IDs from the updatedScopes and update the role
    const scopeIds = updatedScopes.map(scope => scope.id);
    setRole(prev => ({ ...prev, scopes: scopeIds }));
  };

  const handleConnectedResourcesUpdate = (updatedResources: { id: string; label: string }[]) => {
    const resourceIds = updatedResources.map(r => r.id);
    setRole(prev => ({ ...prev, connected_resources: resourceIds }));
  };

  const isComponentResource = (r: Resource) =>
    (r.type && typeof r.type === 'string' ? r.type.toUpperCase() : '') === 'COMPONENT';

  const isPageResource = (r: Resource) => !isComponentResource(r);

  const rolePageResources = roleResources.filter(isPageResource);
  const roleComponentResources = roleResources.filter(isComponentResource);

  const sidebarNavLinksSet = useMemo(
    () => new Set(sidebarNavFlat(role.sidebar_navigation || []).map((i) => i.link)),
    [role.sidebar_navigation]
  );

  const handleAllPagesUpdate = (updated: { id: string; label: string }[]) => {
    const componentIds = roleComponentResources.map(r => r._id).filter(Boolean) as string[];
    setRole(prev => ({ ...prev, connected_resources: [...componentIds, ...updated.map(u => u.id)] }));
  };

  const handleComponentsUpdate = (updated: { id: string; label: string }[]) => {
    const pageIds = rolePageResources.map(r => r._id).filter(Boolean) as string[];
    setRole(prev => ({ ...prev, connected_resources: [...pageIds, ...updated.map(u => u.id)] }));
  };

  const handleSidebarUpdate = React.useCallback((sidebarItems: SidebarNavigationItem[] | (SidebarNavigationItem | SidebarNavigationGroup)[]) => {
    setRole(prev => ({
      ...prev,
      sidebar_navigation: sidebarItems,
    }));
  }, []);

  // Drag handlers for preview modal
  const handlePreviewDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!previewHeaderRef.current) return;
    const rect = previewHeaderRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      const maxX = window.innerWidth - previewSize.width;
      const maxY = window.innerHeight - 50;
      
      setPreviewPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, previewSize]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: previewSize.width,
      height: previewSize.height,
    });
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      const newWidth = Math.max(400, Math.min(resizeStart.width + deltaX, window.innerWidth - previewPosition.x));
      const newHeight = Math.max(300, Math.min(resizeStart.height + deltaY, window.innerHeight - previewPosition.y));
      
      setPreviewSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, previewPosition]);

  const handleEditSubmit = async () => {
    if (!role._id) return;
    setOpenConfirm(false);
    setIsSubmitting(true);
    try {
      const scopeIds = Array.isArray(role.scopes)
        ? role.scopes.map((s: string | { _id?: string }) => typeof s === 'string' ? s : s._id).filter(Boolean) as string[]
        : [];
      const connectedResourceIds = Array.isArray(role.connected_resources)
        ? role.connected_resources.map((cr: string | { _id?: string }) =>
            typeof cr === 'string' ? cr : cr._id
          ).filter(Boolean) as string[]
        : [];

      await editRole(role._id, {
        name: role.name,
        scopes: scopeIds,
        connected_resources: connectedResourceIds,
        group_can_be_attached: role.group_can_be_attached,
        list_can_be_attached: role.list_can_be_attached,
        access_to_all_dealership: role.access_to_all_dealership,
        old_layout: role.old_layout,
        sidebar_navigation: role.sidebar_navigation,
      });
      
      // Update the refreshKey to trigger a re-render in parent component
      setRefreshKey(prev => prev + 1);
      
      // Close the modal
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!role._id) return;
    setOpenDeleteConfirm(false);
    setIsDeleting(true);
    try {
      await deleteRole(role._id);
      
      // Update the refreshKey to trigger a re-render in parent component
      setRefreshKey(prev => prev + 1);
      
      // Close the modal
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setRole(originalData);
    setIsEditing(false);
  };

  const handleClose = () => {
    setIsEditing(false);
    setError("");
    onClose();
  };

  const handleScopeClick = (scope: Scope) => {
    setSelectedScope(scope);
  };

  const handleCloseScopeDetails = () => {
    setSelectedScope(null);
  };

  return (
    <>
      <ModalMAIQ 
        open={open} 
        onClose={handleClose} 
        hideDefaultCloseButton={true}
      >
        <Paper
          elevation={0}
          sx={{
            width: '960px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header - fixed */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#0F2A52',
              color: 'white',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" fontWeight="600">
                Role Details
              </Typography>
              <Box>
                <Tooltip title="Edit Role">
                  <IconButton
                    onClick={() => {
                      setIsEditing(true);
                      setOriginalData(role);
                    }}
                    sx={{ color: 'white', p: 0.5 }}
                    size="small"
                    disabled={isEditing}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Role">
                  <IconButton 
                    onClick={() => setOpenDeleteConfirm(true)} 
                    sx={{ color: 'white', p: 0.5, ml: 1 }}
                    size="small"
                  >
                    {isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      p: 0.5,
                      ml: 1
                    }}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
              ID: {role._id}
              {role._id && <CopyToClipboard text={role._id} size="small" />}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Scrollable content area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              position: 'relative',
              p: 3,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Role Name *
                  </Typography>
                  <TextField
                    fullWidth
                    name="name"
                    value={role.name}
                    onChange={handleChange}
                    variant="outlined"
                    placeholder="Enter role name"
                    disabled={!isEditing}
                    size="small"
                    inputProps={{
                      sx: { py: 1.5 }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Accordion
                  expanded={expandedSidebarResources}
                  onChange={(_, isExpanded) => setExpandedSidebarResources(isExpanded)}
                  sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      minHeight: 48,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      '&.Mui-expanded': { minHeight: 48, backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    },
                    '& .MuiAccordionSummary-content': { margin: '12px 0', alignItems: 'center' },
                    '& .MuiAccordionDetails-root': { pt: 0, pb: 2, px: 2 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">Sidebar Pages</Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        ({sidebarNavCount(role.sidebar_navigation)} assigned)
                      </Typography>
                      {sidebarNavCount(role.sidebar_navigation) > 0 && (
                        <Tooltip title="Preview Sidebar">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setShowPreview(!showPreview); }}
                            sx={{ ml: 0.5, color: showPreview ? 'primary.main' : 'text.secondary', '&:hover': { backgroundColor: 'action.hover' } }}
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {isEditing ? (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.02)', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <SidebarManagement
                          key={role._id ?? 'new'}
                          existingItems={role.sidebar_navigation || []}
                          availableItems={[]}
                          onUpdate={handleSidebarUpdate}
                          oldLayout={!!role.old_layout}
                        />
                      </Paper>
                    ) : sidebarNavCount(role.sidebar_navigation) > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: '300px',
                          '&::-webkit-scrollbar': { width: '8px' },
                          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Label</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Link</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Icon</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...sidebarNavFlat(role.sidebar_navigation)]
                              .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
                              .map((item, index) => {
                                const IconEl = getMaterialIconComponent(item.icon, { fontSize: 20 });
                                return (
                                  <TableRow key={item.link || index}>
                                    <TableCell>{item.label}</TableCell>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.05)', p: 0.5, borderRadius: 0.5, display: 'inline-block' }}>
                                        {item.link}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {IconEl}
                                      <Typography component="span" variant="caption" color="text.secondary">{item.icon || '—'}</Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No sidebar resources. Enable edit to add.</Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Accordion
                  expanded={expandedAllPages}
                  onChange={(_, isExpanded) => setExpandedAllPages(isExpanded)}
                  sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      minHeight: 48,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      '&.Mui-expanded': { minHeight: 48, backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    },
                    '& .MuiAccordionSummary-content': { margin: '12px 0', alignItems: 'center' },
                    '& .MuiAccordionDetails-root': { pt: 0, pb: 2, px: 2 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">Other Pages</Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        ({rolePageResources.length} assigned)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {isEditing ? (
                      <Box sx={{ width: '100%', minHeight: 320 }}>
                        <DualListMAIQ
                          key={`dual-list-all-pages-${role._id}`}
                          availableItems={convertedResources(resources.filter(r => isPageResource(r) && !rolePageResources.some(rr => rr._id === r._id) && !(r.path != null && sidebarNavLinksSet.has(r.path))))}
                          selectedItems={convertedResources(rolePageResources)}
                          onUpdate={handleAllPagesUpdate}
                          listHeight={250}
                        />
                      </Box>
                    ) : (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: '300px',
                          '&::-webkit-scrollbar': { width: '8px' },
                          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Component</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Path</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rolePageResources.length > 0 ? (
                              rolePageResources.map((res) => (
                                <TableRow key={res._id}>
                                  <TableCell>{res.name}</TableCell>
                                  <TableCell>{res.component}</TableCell>
                                  <TableCell sx={{ color: res.path ? 'text.primary' : 'text.disabled', fontStyle: res.path ? 'normal' : 'italic', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                    {res.path || '—'}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.primary' }}>No pages assigned to this role</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Accordion
                  expanded={expandedOtherResources}
                  onChange={(_, isExpanded) => setExpandedOtherResources(isExpanded)}
                  sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      minHeight: 48,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      '&.Mui-expanded': { minHeight: 48, backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    },
                    '& .MuiAccordionSummary-content': { margin: '12px 0', alignItems: 'center' },
                    '& .MuiAccordionDetails-root': { pt: 0, pb: 2, px: 2 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">Components</Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        ({roleComponentResources.length} assigned)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {isEditing ? (
                      <Box sx={{ width: '100%', minHeight: 320 }}>
                        <DualListMAIQ
                          key={`dual-list-components-${role._id}`}
                          availableItems={convertedResources(resources.filter(r => isComponentResource(r) && !roleComponentResources.some(rr => rr._id === r._id)))}
                          selectedItems={convertedResources(roleComponentResources)}
                          onUpdate={handleComponentsUpdate}
                          listHeight={250}
                        />
                      </Box>
                    ) : (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: '300px',
                          '&::-webkit-scrollbar': { width: '8px' },
                          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Component</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {roleComponentResources.length > 0 ? (
                              roleComponentResources.map((res) => (
                                <TableRow key={res._id}>
                                  <TableCell>{res.name}</TableCell>
                                  <TableCell>{res.component}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} align="center" sx={{ fontStyle: 'italic', color: 'text.primary' }}>No components assigned to this role</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Accordion
                  expanded={expandedScopes}
                  onChange={(_, isExpanded) => setExpandedScopes(isExpanded)}
                  sx={{
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      minHeight: 48,
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      '&.Mui-expanded': { minHeight: 48, backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                    },
                    '& .MuiAccordionSummary-content': { margin: '12px 0', alignItems: 'center' },
                    '& .MuiAccordionDetails-root': { pt: 0, pb: 2, px: 2 },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary">Scopes</Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        ({roleScopes.length} assigned)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {isEditing ? (
                      <Box sx={{ width: '100%' }}>
                        {scopes.length > 0 && (
                          <DualListMAIQ
                            key={`dual-list-${scopes.length}-${role._id}`}
                            availableItems={convertedScopes(
                              scopes.filter(scope =>
                                !role.scopes?.some(roleScope => scopeId(roleScope) === scope._id)
                              )
                            )}
                            selectedItems={convertedScopes(roleScopes)}
                            onUpdate={handleScopeUpdate}
                            listHeight={250}
                          />
                        )}
                      </Box>
                    ) : (
                      <TableContainer
                        component={Paper}
                        sx={{
                          maxHeight: '300px',
                          '&::-webkit-scrollbar': { width: '8px' },
                          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Scope</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {roleScopes.length > 0 ? (
                              roleScopes.map((scope) => (
                                <TableRow
                                  key={scope._id}
                                  onClick={() => handleScopeClick(scope)}
                                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }, transition: 'background-color 0.2s ease' }}
                                >
                                  <TableCell>{scope.name}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.05)', p: 0.5, borderRadius: 0.5, display: 'inline-block' }}>
                                      {scope.scope}
                                    </Typography>
                                  </TableCell>
                                  <TableCell><CellChipStatus>{scope.status}</CellChipStatus></TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.primary' }}>No scopes assigned to this role</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Role Options
                </Typography>
                {isEditing ? (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="group_can_be_attached"
                          checked={!!role.group_can_be_attached}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Group can be attached"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="list_can_be_attached"
                          checked={!!role.list_can_be_attached}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="List can be attached"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="access_to_all_dealership"
                          checked={!!role.access_to_all_dealership}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Access to all dealership"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="old_layout"
                          checked={!!role.old_layout}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                      }
                      label="Old layout"
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                    <Typography variant="body2">
                      <b>Group can be attached:</b> {role.group_can_be_attached ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <b>List can be attached:</b> {role.list_can_be_attached ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Access to all dealership:</b> {role.access_to_all_dealership ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2">
                      <b>Old layout:</b> {role.old_layout ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mt: 2, 
                    p: 2, 
                    borderRadius: 1, 
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Metadata
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Creation Date">
                          <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                        </Tooltip>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                          Created: {role.created_at ? formatToLocalTime(role.created_at) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Last Update Date">
                          <UpdateIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                        </Tooltip>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                          Last Updated: {role.updated_at ? formatToLocalTime(role.updated_at) : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider />
          
          {/* Footer with action buttons - fixed at bottom */}
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              zIndex: 9,
              flexShrink: 0,
            }}
          >
            {isEditing && (
              <Tooltip title="Save your changes">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={() => setOpenConfirm(true)}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 'bold', 
                    bgcolor: '#0F2A52',
                    color: 'white',
                    py: 0.75,
                    px: 3
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Confirmation Modals */}
          <ConfirmationDialogMAIQ
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
            onConfirm={handleEditSubmit}
            message="Are you sure you want to save the changes?"
          />
          <ConfirmationDialogMAIQ
            open={openDeleteConfirm}
            onClose={() => setOpenDeleteConfirm(false)}
            onConfirm={handleDeleteRole}
            message="Are you sure you want to delete this role?"
          />
        </Paper>
      </ModalMAIQ>

      {/* Sidebar Preview Modal - Portal to document.body */}
      {showPreview && sidebarNavCount(role.sidebar_navigation) > 0 && createPortal(
        <Box
          sx={{
            position: 'fixed',
            left: `${previewPosition.x}px`,
            top: `${previewPosition.y}px`,
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[8],
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'visible',
            border: '1px solid',
            borderColor: theme.palette.divider,
          }}
        >
          {/* Draggable Header */}
          <Box
            ref={previewHeaderRef}
            onMouseDown={handlePreviewDragStart}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              px: 1.5,
              borderBottom: '1px solid',
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.paper,
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Sidebar Preview
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowPreview(false)}
              sx={{ color: 'text.secondary', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Preview Content - No padding, direct sidebar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              overflow: 'visible',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: role.old_layout ? '72px' : '256px',
                backgroundColor: role.old_layout ? '#0F284C' : theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
                borderRight: role.old_layout ? 'none' : `1px solid ${theme.palette.divider}`,
                borderRadius: role.old_layout ? 0 : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: role.old_layout ? 'center' : 'flex-start',
                pt: role.old_layout ? 2 : 1,
                pb: role.old_layout ? 2 : 1,
                boxShadow: theme.shadows[4],
                border: role.old_layout ? 'none' : `1px solid ${theme.palette.divider}`,
              }}
            >
              {(() => {
                const flatNav = sidebarNavFlat(role.sidebar_navigation);
                const sortedNavigation = [...flatNav].sort((a, b) => {
                  const orderA = a.order !== undefined ? a.order : 999;
                  const orderB = b.order !== undefined ? b.order : 999;
                  return orderA - orderB;
                });

                return role.old_layout ? (
                  // Old Layout - Blue Theme: icon strip only; hover on group opens flyout to the right (like actual sidebar)
                  (() => {
                    const nav = (role.sidebar_navigation || []) as (SidebarNavigationItem | SidebarNavigationGroup)[];
                    const stripBg = '#0F284C';
                    const isGrp = (n: SidebarNavigationItem | SidebarNavigationGroup): n is SidebarNavigationGroup =>
                      'items' in n && Array.isArray((n as SidebarNavigationGroup).items);
                    return (
                      <Box sx={{ position: 'relative', display: 'flex', width: '100%' }}>
                        <Box sx={{ width: 72, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, pb: 2, gap: 0.5 }}>
                          {nav.map((n, idx) => {
                            if (isGrp(n)) {
                              return (
                                <Tooltip key={idx} title={n.label} placement="right" arrow>
                                  <Box
                                    onMouseEnter={() => setPreviewHoveredGroupIndex(idx)}
                                    onMouseLeave={() => setPreviewHoveredGroupIndex(null)}
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      backgroundColor: previewHoveredGroupIndex === idx ? 'rgba(255,255,255,0.12)' : 'transparent',
                                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                    }}
                                  >
                                    {resolveSidebarIcon(n.icon) && React.cloneElement(resolveSidebarIcon(n.icon)!, { sx: { fontSize: 22, color: '#fff' } })}
                                  </Box>
                                </Tooltip>
                              );
                            }
                            const item = n as SidebarNavigationItem;
                            const isDefault = item.isDefault === true;
                            const iconEl = resolveSidebarIcon(item.icon);
                            return (
                              <Tooltip key={item.link ?? idx} title={item.label} placement="right" arrow>
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    backgroundColor: isDefault ? 'rgba(255,255,255,0.15)' : 'transparent',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                                  }}
                                >
                                  {iconEl && React.cloneElement(iconEl, { sx: { fontSize: 22, color: '#fff' } })}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                        {nav.map((grp, idx) => {
                          if (!isGrp(grp)) return null;
                          return (
                            <Fade
                              key={idx}
                              in={previewHoveredGroupIndex === idx}
                              timeout={180}
                              mountOnEnter
                              unmountOnExit
                            >
                              <Box
                                onMouseEnter={() => setPreviewHoveredGroupIndex(idx)}
                                onMouseLeave={() => setPreviewHoveredGroupIndex(null)}
                                sx={{
                                  position: 'absolute',
                                  left: 72,
                                  top: 0,
                                  minWidth: 180,
                                  maxWidth: 260,
                                  background: stripBg,
                                  borderRadius: '0 16px 16px 0',
                                  boxShadow: theme.shadows[12],
                                  py: 2,
                                  px: 2,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 0.5,
                                  color: '#fff',
                                  zIndex: 1,
                                }}
                              >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: '#fff' }}>
                                  {grp.label}
                                </Typography>
                                <Divider sx={{ mb: 0.5, borderColor: 'rgba(255,255,255,0.2)' }} />
                                <List sx={{ p: 0 }}>
                                  {grp.items.map((sub, subIdx) => {
                                    if (isGrp(sub)) {
                                      return (
                                        <Accordion
                                          key={subIdx}
                                          sx={{
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { margin: 0 },
                                          }}
                                        >
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}
                                            sx={{ minHeight: 40, py: 0, '& .MuiAccordionSummary-content': { margin: '4px 0' } }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>
                                                {resolveSidebarIcon(sub.icon) && React.cloneElement(resolveSidebarIcon(sub.icon)!, { sx: { fontSize: 20, color: '#fff' } })}
                                              </ListItemIcon>
                                              <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.875rem' }}>{sub.label}</Typography>
                                            </Box>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ p: 0, pt: 0 }}>
                                            <List sx={{ p: 0 }}>
                                              {sub.items.map((leaf: SidebarNavigationItem | SidebarNavigationGroup) => {
                                                if (isGrp(leaf)) return null;
                                                const leafItem = leaf as SidebarNavigationItem;
                                                return (
                                                  <ListItemButton
                                                    key={leafItem.link}
                                                    sx={{ borderRadius: 2, color: '#fff', py: 0.75, px: 2, pl: 4, mb: 0.5, '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
                                                  >
                                                    <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>
                                                      {resolveSidebarIcon(leafItem.icon) && React.cloneElement(resolveSidebarIcon(leafItem.icon)!, { sx: { fontSize: 18, color: '#fff' } })}
                                                    </ListItemIcon>
                                                    <ListItemText primary={leafItem.label} primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: '#fff' } }} />
                                                  </ListItemButton>
                                                );
                                              })}
                                            </List>
                                          </AccordionDetails>
                                        </Accordion>
                                      );
                                    }
                                    const subItem = sub as SidebarNavigationItem;
                                    const iconSub = resolveSidebarIcon(subItem.icon);
                                    return (
                                      <ListItemButton
                                        key={subItem.link}
                                        sx={{ borderRadius: 2, color: '#fff', py: 1, px: 2, mb: 0.5, '&:hover': { background: 'rgba(255,255,255,0.08)' } }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 32, color: '#fff' }}>
                                          {iconSub && React.cloneElement(iconSub, { sx: { fontSize: 20, color: '#fff' } })}
                                        </ListItemIcon>
                                        <ListItemText primary={subItem.label} primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500, color: '#fff' } }} />
                                      </ListItemButton>
                                    );
                                  })}
                                </List>
                              </Box>
                            </Fade>
                          );
                        })}
                      </Box>
                    );
                  })()
                ) : (
                  // New Layout - Black/White Theme (Full sidebar with labels)
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.5, px: 1.5, pt: 2 }}>
                    {sortedNavigation.map((item, index) => {
                      const iconEl = resolveSidebarIcon(item.icon);
                      const isDefault = item.isDefault === true;
                      return (
                        <Box
                          key={item.link || index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.5,
                            py: 1.2,
                            borderRadius: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: isDefault ? '#000000' : 'transparent',
                            color: isDefault
                              ? '#ffffff'
                              : theme.palette.text.primary,
                            '&:hover': {
                              backgroundColor: isDefault
                                ? '#1a1a1a'
                                : theme.palette.action.hover,
                            },
                          }}
                        >
                          {iconEl && React.cloneElement(iconEl, {
                            sx: { fontSize: 20, color: isDefault ? '#ffffff' : 'inherit' }
                          })}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.875rem',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>
          </Box>

          {/* Resize Handle */}
          <Box
            ref={resizeHandleRef}
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 20,
              height: 20,
              cursor: 'nwse-resize',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 2,
                right: 2,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 0 12px 12px',
                borderColor: `transparent transparent ${theme.palette.text.secondary} transparent`,
              },
            }}
          />
        </Box>,
        document.body
      )}

      {selectedScope ? (
        <ScopeDetailsModal
          open
          onClose={handleCloseScopeDetails}
          scope={selectedScope}
          onSuccess={() => {
            setRefreshKey(prev => prev + 1);
            handleCloseScopeDetails();
          }}
        />
      ) : null}
    </>
  );
};

export default RoleDetailsModal; 